// ResolveScreen — phase 2. After the user finishes scanning, surface
// every yellow + red row so they can bind canonicals, enter brands
// manually, and pick package sizes from the comfort of "not at the
// shelf anymore."
//
// Each row uses TripScanRow (the pre-existing additem primitive) which
// handles canonical search via RisingResults, brand entry via the brand
// chip prompt, axis chips via AxisChipRow + ChipModal, and size via
// PackageSizeStep — all the things the old ShopModePlacementWizard
// couldn't do. When TripScanRow patches an axis, this component routes
// the patch through tripActions.applyScanPatch which writes to
// trip_scans and reclassifies the tier live.
//
// Pair-anchoring is resolved at scan time by the store, so paired rows
// arrive here already canonical-bound (tier=green if the list item had
// a canonical). They show up in green and the user can skip them or
// edit further.

import { useEffect, useMemo, useState } from "react";
import {
  useShopTrip,
  selectUnresolvedScans,
  selectScansArray,
  tripActions,
} from "../../lib/useShopTrip";
import ResolveCard from "./ResolveCard";
import { COLOR, FONT, RADIUS, SPACE } from "../../lib/tokens";
import { useIngredientInfo } from "../../lib/useIngredientInfo";
import { readinessFor } from "../../lib/canonicalReadiness";

const sectionTitle = {
  fontFamily: FONT.mono,
  fontSize: 10,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: COLOR.muted,
  marginBottom: SPACE.md,
};

const ctaPrimaryStyle = {
  flex: 1,
  padding: "12px 16px",
  fontFamily: FONT.mono,
  fontSize: 12,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: COLOR.night,
  background: COLOR.leaf,
  border: "none",
  borderRadius: RADIUS.md,
  cursor: "pointer",
};

const ctaSecondaryStyle = {
  padding: "12px 16px",
  fontFamily: FONT.mono,
  fontSize: 12,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: COLOR.muted,
  background: "transparent",
  border: `1px solid ${COLOR.edge}`,
  borderRadius: RADIUS.md,
  cursor: "pointer",
};

export default function ResolveScreen({ shoppingList = [], onContinue, onBack }) {
  const unresolved = useShopTrip(selectUnresolvedScans);
  const allScans = useShopTrip(selectScansArray);
  const greenCount = allScans.length - unresolved.length;
  const { getInfo } = useIngredientInfo() || {};
  // What's actually missing across unresolved rows. The header used to
  // say "needs a name or canonical" for every non-green row, which lies
  // when the canonical IS bound but brand or size is what's blocking
  // GREEN. Compute the truth so the header tells the user where to look.
  // Also walks all scans (resolved + unresolved) for enrichment gaps so
  // we can warn about rows that will commit without nutrition / density
  // data downstream surfaces depend on.
  const missingSummary = useMemo(() => {
    let needsIdentity = 0;   // no canonical AND no productName
    let needsBrand = 0;      // canonical bound, brand missing
    let needsSize = 0;       // canonical bound, size missing
    let needsEnrichment = 0; // canonical bound but missing nutrition / density
    for (const s of unresolved) {
      const hasIdentity = !!s.canonicalId || !!s.productName;
      if (!hasIdentity) {
        needsIdentity += 1;
        continue;
      }
      if (!s.brand) needsBrand += 1;
      const off = s.offPayload;
      const hasSize = !!(off?.quantity);
      if (!hasSize) needsSize += 1;
    }
    for (const s of allScans) {
      if (!s.canonicalId) continue;
      if (!readinessFor(s.canonicalId, getInfo).ready) needsEnrichment += 1;
    }
    return { needsIdentity, needsBrand, needsSize, needsEnrichment };
  }, [unresolved, allScans, getInfo]);

  function unresolvedHeadline() {
    const n = unresolved.length;
    if (n === 0) return "Everything resolved — ready for checkout.";
    const word = n === 1 ? "item" : "items";
    const verb = n === 1 ? "needs" : "need";
    const parts = [];
    if (missingSummary.needsIdentity) parts.push("a name");
    if (missingSummary.needsBrand) parts.push("a brand");
    if (missingSummary.needsSize) parts.push("a package size");
    if (parts.length === 0) return `${n} ${word} to review.`;
    const joined = parts.length === 1
      ? parts[0]
      : parts.length === 2
        ? `${parts[0]} or ${parts[1]}`
        : `${parts.slice(0, -1).join(", ")}, or ${parts[parts.length - 1]}`;
    return `${n} ${word} ${verb} ${joined}.`;
  }
  // Show-all-rows toggle. ResolveScreen historically hid green rows
  // because they're "done" — but the user routinely wants to verify
  // an auto-resolved row got the right canonical (auto-resolve is
  // pretty good but not perfect, "olive oil" might bind to the wrong
  // canonical when the registry has multiple variants). Off by
  // default to keep the focus on what genuinely needs fixing.
  const [showResolved, setShowResolved] = useState(false);
  const resolvedScans = useMemo(
    () => allScans.filter((s) => s.status === "green"),
    [allScans]
  );

  const listItemById = useMemo(() => {
    const m = new Map();
    for (const it of (shoppingList || [])) m.set(it.id, it);
    return m;
  }, [shoppingList]);

  // On mount, run TWO passes:
  //   1. healScanStatuses — demote any phantom-green rows that don't
  //      actually meet the green contract (canonical + brand + size).
  //      Catches backlog from earlier code that hardcoded status:
  //      "green" without checking the full triple.
  //   2. autoResolveYellow — promote rows whose name binds locally
  //      OR via Haiku to a canonical, after the heal so we don't
  //      try to "promote" a phantom-green back to green.
  // Both are idempotent and only write to the DB when they actually
  // change something.
  useEffect(() => {
    (async () => {
      try {
        await tripActions.healScanStatuses();
      } catch (e) {
        console.warn("[ResolveScreen] healScanStatuses:", e?.message || e);
      }
      try {
        await tripActions.autoResolveYellow();
      } catch (e) {
        console.warn("[ResolveScreen] autoResolveYellow:", e?.message || e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
    }}>
      <div style={{
        padding: "16px 18px",
        borderBottom: `1px solid ${COLOR.edge}`,
        flex: "0 0 auto",
      }}>
        <div style={sectionTitle}>Resolve before checkout</div>
        <div style={{
          fontFamily: FONT.itemTitle,
          fontStyle: "italic",
          fontSize: 22,
          color: COLOR.ink,
        }}>
          {unresolvedHeadline()}
        </div>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginTop: SPACE.xs, gap: 12,
        }}>
          <div style={{
            fontFamily: FONT.forma,
            fontSize: 12,
            color: COLOR.muted,
          }}>
            {greenCount} of {allScans.length} fully resolved
            {missingSummary.needsEnrichment > 0 && (
              <span style={{ color: COLOR.rose, marginLeft: 8 }}>
                · {missingSummary.needsEnrichment} missing nutrition / density
              </span>
            )}
          </div>
          {greenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowResolved((v) => !v)}
              style={{
                background: "transparent",
                border: `1px solid ${COLOR.edge}`,
                color: COLOR.muted,
                padding: "4px 10px",
                borderRadius: 999,
                fontFamily: FONT.mono, fontSize: 9,
                letterSpacing: "0.14em", textTransform: "uppercase",
                cursor: "pointer",
              }}
              className="mcm-focusable"
            >
              {showResolved ? "Hide resolved" : "Show & edit resolved"}
            </button>
          )}
        </div>
      </div>

      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: SPACE.md,
      }}>
        {unresolved.length === 0 && !showResolved ? (
          <div style={{
            padding: SPACE.section,
            fontFamily: FONT.forma,
            fontStyle: "italic",
            fontSize: 14,
            color: COLOR.muted,
            textAlign: "center",
          }}>
            Nothing left to resolve.
          </div>
        ) : (
          <>
            {unresolved.map((scan) => {
              const listItem = scan.pairedShoppingListItemId
                ? listItemById.get(scan.pairedShoppingListItemId)
                : null;
              return (
                <ResolveCard
                  key={scan.id}
                  scan={scan}
                  listItem={listItem}
                />
              );
            })}

            {showResolved && resolvedScans.length > 0 && (
              <>
                <div style={{
                  fontFamily: FONT.mono,
                  fontSize: 9,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: COLOR.muted,
                  marginTop: SPACE.lg,
                  marginBottom: SPACE.xs,
                }}>
                  Resolved · tap any to edit
                </div>
                {resolvedScans.map((scan) => {
                  const listItem = scan.pairedShoppingListItemId
                    ? listItemById.get(scan.pairedShoppingListItemId)
                    : null;
                  return (
                    <div key={scan.id} style={{ opacity: 0.85 }}>
                      <ResolveCard
                        scan={scan}
                        listItem={listItem}
                      />
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>

      <div style={{
        display: "flex",
        gap: SPACE.md,
        padding: `14px 18px calc(env(safe-area-inset-bottom, 0px) + 14px) 18px`,
        borderTop: `1px solid ${COLOR.edge}`,
        flex: "0 0 auto",
      }}>
        {onBack && (
          <button onClick={onBack} style={ctaSecondaryStyle}>Back</button>
        )}
        <button onClick={onContinue} style={ctaPrimaryStyle}>
          {unresolved.length === 0 ? "Continue" : "Skip — continue"}
        </button>
      </div>
    </div>
  );
}
